import type { ListingMetaData, ListingsFilterData, ListingsSearchState } from '@cf/ui-lib';
import { registerSlice } from '../store';

const initialListingsFilterState: ListingsFilterData[] = [];
const initialListingMetaDataState: ListingMetaData = {
	pageNo: 1,
	listingIdsArray: [],
	currentIndex: 0,
};

// Register the slice
const listingsFilterSlice = registerSlice('listingsFilterSlice', initialListingsFilterState);

// Define crud actions for slice
export const addListingsFilter = (item: ListingsFilterData[]) => {
	listingsFilterSlice.update((state) => {
		return [...state, ...item];
	});
};

export const getListingsFilterData = () => {
	let item;
	listingsFilterSlice.subscribe((value) => {
		item = value;
	});
	return item;
};

export const removeListingsFilter = (key: string | string[]) => {
	listingsFilterSlice.update((state) => {
		if (Array.isArray(key)) {
			// If key is an array, filter out any item whose key matches any of the keys in the array
			return state.filter((item) => !key.includes(item.key));
		} else {
			// If key is a single string, filter out the item with that key
			return state.filter((item) => item.key !== key);
		}
	});
};

export const removeAllListingsFilter = () => {
	clearSearchQuery();
	listingsFilterSlice.update(() => {
		return [];
	});
};

export default listingsFilterSlice;

//==================== MetaData slice ===================================

//Registring slice
const listingMetaDtaSlice = registerSlice('listingMetaData', initialListingMetaDataState);

// Define crud actions for slice
export const setNewPage = (item:
	{ pageNo: number; listingIdsArray: string[]; currentIndex?: number; }) => {
	listingMetaDtaSlice.update((state) => {
		return {
			...state,
			currentIndex: 0,
			...item
		};
	});
};

export const setCurrentIndex = (index: number) => {
	listingMetaDtaSlice.update((state: ListingMetaData) => {
		return {
			...state,
			currentIndex: index
		};
	});
};

export const setListingIds = (listingIds: string[]) => {
	listingMetaDtaSlice.update((state: ListingMetaData) => {
		return {
			...state,
			listingIdsArray: listingIds
		};
	});
};

export const setNextIndex = () => {
	listingMetaDtaSlice.update((state: ListingMetaData) => {
		const currentListing = state.currentIndex || 0;
		const length = state.pageLength || 0;
		return {
			...state,
			currentIndex: length > currentListing + 1 ? currentListing + 1 : currentListing
		};
	});
};

export const setPreviousIndex = () => {
	listingMetaDtaSlice.update((state: ListingMetaData) => {
		const currentListing = state.currentIndex || 0;
		return {
			...state,
			currentIndex: currentListing > 0 ? currentListing - 1 : currentListing
		};
	});
};

export const getListingMetadata = (): ListingMetaData => {
	let item: ListingMetaData;
	listingMetaDtaSlice.subscribe((value: ListingMetaData) => {
		item = value;
	});
	return item;
};

export const clearListingMetadata = () => {
	listingMetaDtaSlice.update(() => {
		return {};
	});
};

export const changeSelectedListingIds = (ids: string[]) => {
	listingMetaDtaSlice.update((state: ListingMetaData) => {
		return {
			...state,
			selectedListingIds: ids,
		};

	});
};

export const setLastViewedListing = (listingId: string) => {
	listingMetaDtaSlice.update((state: ListingMetaData) => {
		return {
			...state,
			lastViewedListingId: listingId
		};
	});
};

export const clearLastViewedListing = () => {
	listingMetaDtaSlice.update((state: ListingMetaData) => {
		return {
			...state,
			lastViewedListingId: undefined
		};
	});
};

export const setBradsHavingIPs = (brandsHavingIPs: {
	tmBrandIds: string[];
	cpBrandIds: string[];
	ropBrandIds: string[];
}) => {
	listingMetaDtaSlice.update((state: ListingMetaData) => {
		return {
			...state,
			brandsHavingIPs: brandsHavingIPs
		};
	});
};

export const clearBradsHavingIPs = () => {
	listingMetaDtaSlice.update((state: ListingMetaData) => {
		return {
			...state,
			brandsHavingIPs: { tmBrandIds: [], cpBrandIds: [], ropBrandIds: [] }
		};
	});
};

export const setBrandWiseListingIds = (brandWiseListingIds: { [key: string]: string[] }) => {
	listingMetaDtaSlice.update((state: ListingMetaData) => {
		return {
			...state,
			brandWiseListingIds: brandWiseListingIds
		};
	});
};

//==================== Listings search slice ===================================

const initialListingsSearchState: ListingsSearchState = {
	searchQuery: '',
};

export const listingsSearchSlice = registerSlice('listingsSearchSlice', initialListingsSearchState);

export const setSearchQuery = (query: string) => {
	listingsSearchSlice.update((state) => {
		return {
			...state,
			searchQuery: query,
		};
	});
};

export const clearSearchQuery = () => {
	listingsSearchSlice.update((state) => {
		return {
			...state,
			searchQuery: '',
		};
	});
};